import React from 'react';
import { FormattedMessage } from 'react-intl';

import { Group } from '../organisms/group';
import { ParentPaper } from '../atoms/parent-paper';
import { SliderBox } from '../molecules/slider-box';
import { SwitchBox } from '../molecules/switch-box';
import { ValueBox } from '../molecules/value-box';
import { FormattedValue } from '../store/formatted-value';
import { Battery } from '../atoms/icons/battery';
import { SelectBox } from '../molecules/select-box';
import { CloudPlugsList } from '../organisms/cloud-plugs-list';
import { HelpButton } from '../atoms/help-button';
import { SolarPanel } from '../atoms/icons/solar-panel';

const CloudPlugs = () => {
  return (
    <>
      <Group
        layout="list"
        title={
          <FormattedMessage id="cloud_plugs.title" defaultMessage="Zásuvky" />
        }
      >
        <CloudPlugsList />
      </Group>
      <CloudPlugsBattery />
      <CloudPlugsControlCondition />
      <CloudPlugsTime />
      <CloudPlugsOverflow />
      <CloudPlugsPVPower />
      <CloudPlugsLedMode />
    </>
  );
};

const CloudPlugsBattery = () => {
  return (
    <Group
      title={
        <FormattedMessage
          id="cloud_plugs.bat"
          defaultMessage="Řízení podle baterie"
        />
      }
      layout="list"
      visibility={{ variableId: 'PV_BATTERY' }}
    >
      <ParentPaper>
        <SwitchBox
          title={
            <FormattedMessage
              id="cloud_plugs.battery.on"
              defaultMessage="Aktivní"
            />
          }
          switchVariableId="CLOUD_PLUGS_BATTERY_CONTROLLED"
          fullWidth
        />
        <SliderBox
          title={
            <FormattedMessage
              id="cloud_plugs.battery.battery_start"
              defaultMessage="Zapnout při nabití"
            />
          }
          subtitle={
            <FormattedMessage
              id="cloud_plugs.battery.battery_start.subtitle"
              defaultMessage="Nabití baterie při kterém se aktivuje zásuvka"
            />
          }
          visibility={{ variableId: 'CLOUD_PLUGS_BATTERY_CONTROLLED' }}
          variableId="CLOUD_PLUGS_BATTERY_START"
          opts={{ style: 'percent', maximumFractionDigits: 0 }}
          min={20}
          max={99}
        />
        <SliderBox
          title={
            <FormattedMessage
              id="cloud_plugs.battery.battery_stop"
              defaultMessage="Vypnout při nabití"
            />
          }
          subtitle={
            <FormattedMessage
              id="cloud_plugs.battery.battery_stop.subtitle"
              defaultMessage="Nabití baterie při kterém se vypne zásuvka"
            />
          }
          visibility={{ variableId: 'CLOUD_PLUGS_BATTERY_CONTROLLED' }}
          variableId="CLOUD_PLUGS_BATTERY_STOP"
          opts={{ style: 'percent', maximumFractionDigits: 0 }}
          min={20}
        />
        <ValueBox
          type="row"
          title={
            <FormattedMessage
              id="cloud_plugs.battery_charged"
              defaultMessage="Nabití baterie"
            />
          }
          value={
            <FormattedValue
              variableId="PV_ACTUAL_SOC"
              opts={{
                style: 'percent',
                maximumFractionDigits: 0,
              }}
            />
          }
          icon={<Battery />}
          visibility={{
            variableIds: ['PV_BATTERY', 'CLOUD_PLUGS_BATTERY_CONTROLLED'],
          }}
        />
      </ParentPaper>
    </Group>
  );
};

const CloudPlugsControlCondition = () => {
  return (
    <Group
      layout="list"
      visibility={{
        variableIds: [
          'CLOUD_PLUGS_BATTERY_CONTROLLED',
          'CLOUD_PLUGS_TIME_CONTROLLED',
          'CLOUD_PLUGS_OVERFLOW_CONTROLLED',
        ],
      }}
    >
      <SelectBox
        title={
          <FormattedMessage
            id="cloud_plugs.condition"
            defaultMessage="Podmínka"
          />
        }
        selectVariableId="CLOUD_PLUGS_CONTROL_CONDITION"
        options={[
          {
            label: (
              <FormattedMessage
                id="cloud_plugs.condition.and"
                defaultMessage="A"
              />
            ),
            value: 'and', // TODO: use enums
          },
          {
            label: (
              <FormattedMessage
                id="cloud_plugs.condition.or"
                defaultMessage="Nebo"
              />
            ),
            value: 'or',
          },
        ]}
        fullWidth
      />
    </Group>
  );
};

const CloudPlugsTime = () => {
  return (
    <Group
      title={
        <FormattedMessage
          id="cloud_plugs.time"
          defaultMessage="Řízení podle času"
        />
      }
      layout="list"
    >
      <ParentPaper>
        <SwitchBox
          title={
            <FormattedMessage
              id="cloud_plugs.time.on"
              defaultMessage="Aktivní"
            />
          }
          switchVariableId="CLOUD_PLUGS_TIME_CONTROLLED"
          fullWidth
        />
        <SliderBox
          type="time"
          title={
            <FormattedMessage
              id="cloud_plugs.turn_on"
              defaultMessage="Zapnout"
            />
          }
          variableId="CLOUD_PLUGS_TIME_START"
          visibility={{ variableId: 'CLOUD_PLUGS_TIME_CONTROLLED' }}
          track={false}
        />
        <SliderBox
          type="time"
          title={
            <FormattedMessage
              id="cloud_plugs.turn_off"
              defaultMessage="Vypnout"
            />
          }
          variableId="CLOUD_PLUGS_TIME_STOP"
          visibility={{ variableId: 'CLOUD_PLUGS_TIME_CONTROLLED' }}
          track={false}
        />
      </ParentPaper>
    </Group>
  );
};

const CloudPlugsOverflow = () => {
  return (
    <Group
      title={
        <>
          <FormattedMessage
            id="cloud_plugs.overflow"
            defaultMessage="Řízení podle přetoku"
          />
          <HelpButton
            variant="dialog"
            title={
              <FormattedMessage
                id="cloud_plugs.overflow"
                defaultMessage="Řízení podle přetoku"
              />
            }
            content={
              <FormattedMessage
                id="cloud_plugs.overflow.help"
                defaultMessage="Zapínání zásuvky při definovaném přetoku do distribuční sítě."
              />
            }
          />
        </>
      }
      layout="list"
    >
      <ParentPaper>
        <SwitchBox
          title={
            <FormattedMessage
              id="cloud_plugs.overflow.on"
              defaultMessage="Aktivní"
            />
          }
          switchVariableId="CLOUD_PLUGS_OVERFLOW_CONTROLLED"
          fullWidth
        />
        <SliderBox
          title={
            <>
              <FormattedMessage
                id="cloud_plugs.overflow.startPower"
                defaultMessage="Zapnout při odběru ze sítě"
              />
              <HelpButton
                variant="dialog"
                title={
                  <FormattedMessage
                    id="cloud_plugs.overflow.startPower"
                    defaultMessage="Zapnout při odběru ze sítě"
                  />
                }
                content={
                  <FormattedMessage
                    id="cloud_plugs.overflow.startPower.help"
                    defaultMessage="Zásuvka se zapne, pokud je přetok v nastavené výši po definovaný interval zpoždění. Pokud je hodnota nastavena na - 1500W a zpoždění zapnutí je 5 minut, tak zásuvka sepne při přetoku do sítě ve výši alespoň 1500W po dobu 5 minut."
                  />
                }
              />
            </>
          }
          subtitle={
            <FormattedMessage
              id="cloud_plugs.overflow.startPower.subtitle"
              defaultMessage="Záporná hodnota je přetok, kladná hodnota je odběr"
            />
          }
          variableId="CLOUD_PLUGS_OVERFLOW_START_POWER"
          visibility={{ variableId: 'CLOUD_PLUGS_OVERFLOW_CONTROLLED' }}
          opts={{
            style: 'unit',
            unit: 'kilowatt',
            maximumFractionDigits: 1,
          }}
          step={0.1}
          min={-15}
          max={0}
          track={false}
        />
        <SliderBox
          title={
            <>
              <FormattedMessage
                id="cloud_plugs.overflow.stopPower"
                defaultMessage="Vypnout při odběru ze sítě"
              />
              <HelpButton
                variant="dialog"
                title={
                  <FormattedMessage
                    id="cloud_plugs.overflow.stopPower"
                    defaultMessage="Vypnout při odběru ze sítě"
                  />
                }
                content={
                  <FormattedMessage
                    id="cloud_plugs.overflow.stopPower.help"
                    defaultMessage="Zásuvka se vypne, pokud je odběr vyšší než nastavený po definovaný interval zpoždění. Pokud je hodnota nastavena na 100W a zpoždění vypnutí je 5 minut, tak zásuvka vypne při odběru ze sítě ve výši minimálně 100W po dobu 5 minut."
                  />
                }
              />
            </>
          }
          subtitle={
            <FormattedMessage
              id="cloud_plugs.overflow.stopPower.subtitle"
              defaultMessage="Záporná hodnota je přetok, kladná hodnota je odběr"
            />
          }
          variableId="CLOUD_PLUGS_OVERFLOW_STOP_POWER"
          visibility={{ variableId: 'CLOUD_PLUGS_OVERFLOW_CONTROLLED' }}
          opts={{
            style: 'unit',
            unit: 'kilowatt',
            maximumFractionDigits: 1,
          }}
          step={0.1}
          min={-10}
          max={5}
          track={false}
        />
      </ParentPaper>
    </Group>
  );
};

const CloudPlugsPVPower = () => {
  return (
    <Group
      title={
        <>
          <FormattedMessage
            id="cloud_plugs.pv_power"
            defaultMessage="Řízení podle výkonu FVE"
          />
          <HelpButton
            variant="dialog"
            title={
              <FormattedMessage
                id="cloud_plugs.pv_power"
                defaultMessage="Řízení podle výkonu FVE"
              />
            }
            content={
              <FormattedMessage
                id="cloud_plugs.pv_power.help"
                defaultMessage="Zapínání zásuvky při definovaném výkonu FVE."
              />
            }
          />
        </>
      }
      layout="list"
    >
      <ParentPaper>
        <SwitchBox
          title={
            <FormattedMessage
              id="cloud_plugs.pv_power.on"
              defaultMessage="Aktivní"
            />
          }
          switchVariableId="CLOUD_PLUGS_PV_POWER_CONTROLLED"
          fullWidth
        />
        <SliderBox
          title={
            <>
              <FormattedMessage
                id="cloud_plugs.pv_power.startPower"
                defaultMessage="Zapnout při výrobě FVE"
              />
              <HelpButton
                variant="dialog"
                title={
                  <FormattedMessage
                    id="cloud_plugs.pv_power.startPower"
                    defaultMessage="Zapnout při výrobě FVE"
                  />
                }
                content={
                  <FormattedMessage
                    id="cloud_plugs.pv_power.startPower.help"
                    defaultMessage="Zásuvka se zapne, pokud je výkon FVE větší než nastavená hodnota. Pokud je hodnota nastavena na 1000W, tak se zásuvka sepne při výrobě FVE ve výši alespoň 1000W."
                  />
                }
              />
            </>
          }
          variableId="CLOUD_PLUGS_PV_POWER_START_POWER"
          visibility={{ variableId: 'CLOUD_PLUGS_PV_POWER_CONTROLLED' }}
          opts={{
            style: 'unit',
            unit: 'kilowatt',
            maximumFractionDigits: 1,
          }}
          step={0.1}
          min={0}
          max={50}
          track={false}
        />
        <SliderBox
          title={
            <>
              <FormattedMessage
                id="cloud_plugs.pv_power.stopPower"
                defaultMessage="Vypnout při výrobě FVE"
              />
              <HelpButton
                variant="dialog"
                title={
                  <FormattedMessage
                    id="cloud_plugs.pv_power.stopPower"
                    defaultMessage="Vypnout při výrobě FVE"
                  />
                }
                content={
                  <FormattedMessage
                    id="cloud_plugs.pv_power.stopPower.help"
                    defaultMessage="Zásuvka se vypne, pokud je výkon FVE menší než nastavená hodnota. Pokud je hodnota nastavena na 500W, tak se zásuvka vypne při výrobě FVE nižší než 500W."
                  />
                }
              />
            </>
          }
          variableId="CLOUD_PLUGS_PV_POWER_STOP_POWER"
          visibility={{ variableId: 'CLOUD_PLUGS_PV_POWER_CONTROLLED' }}
          opts={{
            style: 'unit',
            unit: 'kilowatt',
            maximumFractionDigits: 1,
          }}
          step={0.1}
          min={0}
          max={50}
          track={false}
        />
        <ValueBox
          title={
            <FormattedMessage
              id="photovoltaics.common.power"
              defaultMessage="Aktuální výkon"
            />
          }
          value={
            <FormattedValue
              variableId="PV_ACTUAL_POWER"
              opts={{
                style: 'unit',
                unit: 'kilowatt',
                maximumFractionDigits: 2,
                minimumFractionDigits: 2,
              }}
            />
          }
          visibility={{ variableId: 'CLOUD_PLUGS_PV_POWER_CONTROLLED' }}
          icon={<SolarPanel />}
          type="row"
        />
      </ParentPaper>
    </Group>
  );
};

const CloudPlugsLedMode = () => {
  return (
    <Group
      title={
        <FormattedMessage
          id="cloud_plugs.led_mode.title"
          defaultMessage="LED signalizace"
        />
      }
      layout="list"
    >
      <ParentPaper>
        <SwitchBox
          title={
            <FormattedMessage
              id="cloud_plugs.led_mode.battery.switch.title"
              defaultMessage="Podsvícení při sepnuté zásuvce"
            />
          }
          switchVariableId="CLOUD_PLUGS_LED_MODE_SWITCH"
          fullWidth
          subtitle={
            <FormattedMessage
              id="cloud_plugs.led_mode.switch.subtitle"
              defaultMessage="Podsvícení aktivní, pokud je zásuvka sepnutá"
            />
          }
        />
        <SwitchBox
          title={
            <FormattedMessage
              id="cloud_plugs.led_mode.battery.title"
              defaultMessage="Podsvícení při nabité baterii"
            />
          }
          switchVariableId="CLOUD_PLUGS_LED_MODE_BATTERY"
          visibility={{
            variableIds: [
              { variableId: 'CLOUD_PLUGS_LED_MODE_SWITCH', reversed: true },
              { variableId: 'PV_BATTERY' },
            ],
          }}
          fullWidth
          subtitle={
            <FormattedMessage
              id="cloud_plugs.led_mode.battery.subtitle"
              defaultMessage="Podsvícení aktivní, pokud má baterie alespoň 95%"
            />
          }
        />
        <SwitchBox
          title={
            <FormattedMessage
              id="cloud_plugs.led_mode.overflow.title"
              defaultMessage="Podsvícení při přetoku"
            />
          }
          switchVariableId="CLOUD_PLUGS_LED_MODE_OVERFLOW"
          visibility={{
            variableIds: [
              { variableId: 'CLOUD_PLUGS_LED_MODE_SWITCH', reversed: true },
            ],
          }}
          fullWidth
          subtitle={
            <FormattedMessage
              id="cloud_plugs.led_mode.overflow.subtitle"
              defaultMessage="Podsvícení aktivní, pokud dojde ke stabilnímu přetoku"
            />
          }
        />
      </ParentPaper>
    </Group>
  );
};

export default CloudPlugs;
